'use client';

import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { ChevronsUpDownIcon, XIcon, CheckIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Input, inputVariants } from '@/components/ui/input';
import { RemoveScroll } from 'react-remove-scroll';

export const ComboboxContext = React.createContext({
  chipsRef: null,
  multiple: false,
  disabled: false
});

export function Combobox({ className, disabled, ...props }) {
  const chipsRef = React.useRef(null);
  return (
    <ComboboxContext.Provider value={{ chipsRef, multiple: !!props.multiple, disabled }}>
      <ComboboxPrimitive.Root
        disabled={disabled}
        className={cn(disabled && 'cursor-not-allowed', 'data-[disabled]:cursor-not-allowed', className)}
        {...props}
      />
    </ComboboxContext.Provider>
  );
}

export function ComboboxChipsInput({ className, size, ...props }) {
  const sizeValue = size ?? 'default';
  const { disabled } = React.useContext(ComboboxContext);

  return (
    <ComboboxPrimitive.Input
      className={cn(
        'min-w-6 flex-1 bg-transparent ps-0 text-base outline-none placeholder:text-neutral-light sm:text-sm',
        // Seletor para quando houver um chip antes do input
        '[&[data-slot=combobox-chip]+&]:ps-0.5',
        disabled && '!cursor-not-allowed',
        className
      )}
      data-size={typeof sizeValue === 'string' ? sizeValue : undefined}
      data-slot="combobox-chips-input"
      size={typeof sizeValue === 'number' ? sizeValue : undefined}
      {...props}
    />
  );
}

export function ComboboxInput({ className, showTrigger = true, showClear = false, startAddon, size, triggerProps, clearProps, ...props }) {
  const sizeValue = size ?? 'default';
  const { chipsRef } = React.useContext(ComboboxContext);

  return (
    <ComboboxPrimitive.InputGroup
      ref={chipsRef}
      className={cn('relative w-full text-foreground data-[disabled]:opacity-60', 'data-[disabled]:cursor-not-allowed', className)}
      data-slot="combobox-input-group"
    >
      {startAddon && (
        <div
          aria-hidden="true"
          className={cn('pointer-events-none absolute inset-y-0 start-0 z-10 flex items-center justify-center opacity-80', sizeValue === 'sm' ? 'ps-2' : 'ps-3', '[&_svg]:size-4')}
          data-slot="combobox-start-addon"
        >
          {startAddon}
        </div>
      )}
      <ComboboxPrimitive.Input
        className={cn(
          // Ajuste de padding quando há addon inicial
          startAddon && (sizeValue === 'sm' ? 'ps-7' : 'ps-8.5'),
          // Ajuste de padding quando há trigger ou botão de limpar
          (showTrigger || showClear) && (sizeValue === 'sm' ? 'pe-7' : 'pe-8'),
          className
        )}
        data-slot="combobox-input"
        render={
          <Input
            className="data-[disabled]:opacity-100"
            nativeInput
            size={sizeValue}
          />
        }
        {...props}
      />
      {showTrigger && (
        <ComboboxTrigger
          className={cn(
            'absolute top-1/2 inline-flex shrink-0 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border border-transparent opacity-80 outline-none transition-opacity hover:opacity-100 sm:size-7 [&_svg]:pointer-events-none [&_svg]:size-4',
            sizeValue === 'sm' ? 'end-0.5 size-7' : 'end-1 size-8'
          )}
          {...triggerProps}
        >
          <ComboboxPrimitive.Icon
            data-slot="combobox-icon"
            className="leading-[0]"
          >
            <ChevronsUpDownIcon />
          </ComboboxPrimitive.Icon>
        </ComboboxTrigger>
      )}
      {showClear && (
        <ComboboxClear
          className={cn(
            'absolute top-1/2 inline-flex shrink-0 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border border-transparent opacity-80 outline-none transition-opacity hover:opacity-100 sm:size-7 [&_svg]:pointer-events-none [&_svg]:size-4',
            sizeValue === 'sm' ? 'end-0.5 size-7' : 'end-1 size-8'
          )}
          {...clearProps}
        >
          <XIcon />
        </ComboboxClear>
      )}
    </ComboboxPrimitive.InputGroup>
  );
}

export function ComboboxTrigger({ className, children, ...props }) {
  return (
    <ComboboxPrimitive.Trigger
      className={className}
      data-slot="combobox-trigger"
      {...props}
    >
      {children}
    </ComboboxPrimitive.Trigger>
  );
}

export function ComboboxPopup({ className, children, side = 'bottom', sideOffset = 4, alignOffset, align = 'start', anchor: anchorProp, ...props }) {
  const { chipsRef } = React.useContext(ComboboxContext);
  const anchor = anchorProp ?? chipsRef;

  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className={cn('pointer-events-auto z-[1100] select-none overscroll-contain', className)}
        data-slot="combobox-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <RemoveScroll
          enabled
          removeScrollBar={false}
          allowPinchZoom
        >
          <span className={cn('relative flex flex-col rounded-lg border bg-popover shadow-md transition-[scale,opacity]', 'w-[var(--anchor-width)] max-w-[var(--available-width)]', className)}>
            <ComboboxPrimitive.Popup
              className="flex flex-col overflow-hidden text-foreground"
              data-slot="combobox-popup"
              {...props}
            >
              {children}
            </ComboboxPrimitive.Popup>
          </span>
        </RemoveScroll>
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

export function ComboboxItem({ className, children, ...props }) {
  return (
    <ComboboxPrimitive.Item
      className={cn(
        'group grid min-h-[2.25rem] cursor-default grid-cols-[1.25rem_1fr] items-center gap-2 rounded-sm py-1.5 pe-4 ps-2 text-base outline-none transition-colors sm:min-h-[2rem] sm:text-sm',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        'data-[selected]:bg-accent/50',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        className
      )}
      data-slot="combobox-item"
      {...props}
    >
      <div className="flex items-center justify-center opacity-0 group-data-[selected]:opacity-100">
        <CheckIcon className="size-3.5" />
      </div>
      <div className="col-start-2">{children}</div>
    </ComboboxPrimitive.Item>
  );
}

export function ComboboxSeparator({ className, ...props }) {
  return (
    <ComboboxPrimitive.Separator
      className={cn('mx-1 my-1 h-px bg-border last:hidden', className)}
      data-slot="combobox-separator"
      {...props}
    />
  );
}

export function ComboboxGroup({ className, ...props }) {
  return (
    <ComboboxPrimitive.Group
      className={cn('p-1', className)}
      data-slot="combobox-group"
      {...props}
    />
  );
}

export function ComboboxGroupLabel({ className, ...props }) {
  return (
    <ComboboxPrimitive.GroupLabel
      className={cn('px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground', className)}
      data-slot="combobox-group-label"
      {...props}
    />
  );
}

export function ComboboxEmpty({ className, ...props }) {
  return (
    <ComboboxPrimitive.Empty
      className={cn('text-center text-sm text-muted-foreground empty:hidden [&:not(:empty)]:py-2', className)}
      data-slot="combobox-empty"
      {...props}
    />
  );
}

export function ComboboxRow({ className, ...props }) {
  return (
    <ComboboxPrimitive.Row
      className={className}
      data-slot="combobox-row"
      {...props}
    />
  );
}

export function ComboboxValue({ ...props }) {
  return (
    <ComboboxPrimitive.Value
      data-slot="combobox-value"
      {...props}
    />
  );
}

export function ComboboxList({ className, ...props }) {
  return (
    <ComboboxPrimitive.List
      className={cn('max-h-[15rem] overflow-y-auto p-1 data-[empty]:hidden', className)}
      data-slot="combobox-list"
      {...props}
    />
  );
}

export function ComboboxClear({ className, ...props }) {
  return (
    <ComboboxPrimitive.Clear
      className={className}
      data-slot="combobox-clear"
      {...props}
    />
  );
}

export function ComboboxStatus({ className, ...props }) {
  return (
    <ComboboxPrimitive.Status
      className={cn('px-3 py-2 text-xs font-medium text-muted-foreground', className)}
      data-slot="combobox-status"
      {...props}
    />
  );
}

export function ComboboxCollection(props) {
  return (
    <ComboboxPrimitive.Collection
      data-slot="combobox-collection"
      {...props}
    />
  );
}

export function ComboboxChips({ className, children, startAddon, ...props }) {
  const { chipsRef, disabled } = React.useContext(ComboboxContext);

  return (
    <ComboboxPrimitive.Chips
      className={cn('!h-auto min-h-10 flex-wrap gap-2', inputVariants(), disabled && 'cursor-not-allowed opacity-60 [&_*]:cursor-not-allowed', className)}
      data-slot="combobox-chips"
      ref={chipsRef}
      {...props}
    >
      {startAddon && (
        <div
          aria-hidden="true"
          className="flex shrink-0 items-center ps-1.5 opacity-80 [&_svg]:size-4"
          data-slot="combobox-start-addon"
        >
          {startAddon}
        </div>
      )}
      {children}
    </ComboboxPrimitive.Chips>
  );
}

export function ComboboxChip({ children, removeProps, ...props }) {
  return (
    <ComboboxPrimitive.Chip
      className="flex items-center gap-1 rounded bg-primary-lightest py-0.5 pe-1 ps-2 text-xs font-medium text-accent-foreground outline-none transition-colors hover:bg-primary-lighter"
      data-slot="combobox-chip"
      {...props}
    >
      {children}
      <ComboboxChipRemove {...removeProps} />
    </ComboboxPrimitive.Chip>
  );
}

export function ComboboxChipRemove(props) {
  return (
    <ComboboxPrimitive.ChipRemove
      aria-label="Remove"
      className="flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm opacity-70 transition-opacity hover:bg-black/10 hover:opacity-100 dark:hover:bg-white/10"
      data-slot="combobox-chip-remove"
      {...props}
    >
      <XIcon className="size-3" />
    </ComboboxPrimitive.ChipRemove>
  );
}

export const useComboboxFilter = ComboboxPrimitive.useFilter;

export { ComboboxPrimitive };
